import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminQualityRebackfillButton — admin-only, dashboard-only.
 *
 * Calls POST /api/admin/backfill-quality-scores which walks the entire
 * stock_cache and re-applies compute_quality_score() against every
 * doc's full merged state. Use after a regression in the scoring
 * formula or whenever the Min-Quality screener filter and the on-row
 * pill drift out of sync. Pure local Mongo work — no external API spend.
 *
 * Displays "Adj : N" inline showing the number of stocks whose
 * quality_score actually changed during the most recent run. Number
 * is read from /admin/massive-stats on mount (so it persists across
 * page loads) and updated immediately after a click.
 *
 * Non-admin users see nothing (early return).
 */
export default function AdminQualityRebackfillButton() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [adjusted, setAdjusted] = useState(null);

  // Round 102f (Feb 2026) — Poll status endpoint while a background
  // rebackfill is running. Endpoint now returns `{in_progress,
  // last_adjusted, last_finished_at}` so we can flip the button
  // state without a page refresh.
  const refreshStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/backfill-quality-scores/status`, { withCredentials: true });
      if (typeof data?.last_adjusted === 'number') setAdjusted(data.last_adjusted);
      return !!data?.in_progress;
    } catch {
      return false;
    }
  };

  // Pull the last-run "adjusted" count on mount so the pill is
  // already populated when an admin lands on the dashboard.
  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    let cancelled = false;
    // First try the new status endpoint; fall back to the massive-stats
    // path for backward compatibility with un-redeployed environments.
    axios.get(`${API}/admin/backfill-quality-scores/status`, { withCredentials: true })
      .then(({ data }) => {
        if (cancelled) return;
        if (typeof data?.last_adjusted === 'number') setAdjusted(data.last_adjusted);
        if (data?.in_progress) setBusy(true);
      })
      .catch(() => {
        axios.get(`${API}/admin/massive-stats`, { withCredentials: true })
          .then(({ data }) => {
            if (cancelled) return;
            if (typeof data?.quality_adjusted === 'number') setAdjusted(data.quality_adjusted);
          })
          .catch(() => { /* silent — button still works */ });
      });
    return () => { cancelled = true; };
  }, [user]);

  if (!user || user.role !== 'admin') return null;

  const onClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/backfill-quality-scores`, {}, { withCredentials: true }
      );
      if (data?.status === 'already_running') {
        toast.info('Quality rebackfill already running', { description: 'Another admin fired it recently. Polling for completion…' });
      } else {
        toast.success('Quality rebackfill started', {
          description: 'Running in the background — should finish in ~30-60 seconds. This pill updates when done.',
          duration: 6000,
        });
      }
      // Poll every 5s until the background task reports done.
      let ticks = 0;
      const interval = setInterval(async () => {
        ticks += 1;
        const stillRunning = await refreshStatus();
        if (!stillRunning || ticks >= 60) {
          clearInterval(interval);
          setBusy(false);
          if (!stillRunning && ticks > 0) {
            toast.success('Quality rebackfill complete', {
              description: 'Adjusted count updated on the pill. Screener Min-Quality filter is now in sync.',
              duration: 6000,
            });
          }
        }
      }, 5000);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Rebackfill failed';
      toast.error('Could not start rebackfill', { description: msg });
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-muted-foreground hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors px-2 py-1 rounded-md hover:bg-emerald-50 dark:hover:bg-emerald-950/30 border border-dashed border-muted-foreground/30 disabled:opacity-50"
      title="Admin only: walk the entire stock_cache and recompute Dividend Quality Score (0-100) for every ticker. Use when the Screener Min-Quality slider drops a stock that the row pill says is healthy. Pure local Mongo work — no external API spend. Takes ~5-10 seconds."
      data-testid="admin-quality-rebackfill-btn"
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
      Admin: Rebackfill Quality
      {adjusted !== null && (
        <span className="ml-1 text-emerald-600 dark:text-emerald-400 font-mono" data-testid="admin-quality-adjusted-count">
          Adj : {adjusted}
        </span>
      )}
    </button>
  );
}
